import React, { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { brand, nav, cta, media } from "../../content/siteContent";
import { CTAButton } from "./primitives";
import { scrollToId } from "../../hooks/useLenis";
import { track, EVENTS } from "../../lib/analytics";

const AXIOM_URL = "https://www.axiomcollege.com.au/";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const phoneHref = `tel:${brand.phone.replace(/\s+/g, "")}`;
  const onPhone = () => track(EVENTS.PHONE_CALL, { location: "header" });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  const requestProspectus = () => {
    track(EVENTS.PROSPECTUS_CTA, { location: "header" });
    go("prospectus");
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50"
    >
      {/* Compliance strip — always dark, at the very top */}
      <div className="bg-[#081321] border-b border-hair-dark" data-testid="compliance-strip">
        <div className="container-page py-1.5">
          <p className="text-[11px] sm:text-xs leading-snug text-center sm:text-left">
            <span className="hidden sm:inline font-display font-semibold text-white">{brand.name}</span>
            <span className="hidden sm:inline text-white/50"> — </span>
            <span className="hidden sm:inline text-white/65">{brand.complianceLine}</span>
            <span className="sm:hidden text-white/70">Delivered &amp; assessed by Axiom College · RTO 40489</span>
          </p>
        </div>
      </div>

      <div
        className={`transition-colors duration-300 ${
          scrolled ? "bg-[#F6F8FA]/90 backdrop-blur-xl border-b border-hair" : "bg-transparent"
        }`}
      >
        <div className="container-page flex items-center justify-between gap-x-6 h-[72px]">
          <div className="flex items-center gap-3">
            <a
              href={AXIOM_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Axiom College — visit axiomcollege.com.au (opens in a new tab)"
              className="focus-ring rounded-sm shrink-0"
              data-testid="axiom-logo-link"
            >
              <img
                src={scrolled ? media.logo : media.logoWhite}
                alt="Axiom College — RTO 40489"
                className="h-10 w-auto"
                data-testid="axiom-logo"
              />
            </a>
            <button
              data-testid="brand-wordmark"
              onClick={() => go("top")}
              className="hidden sm:flex items-center gap-3 text-left focus-ring rounded-sm"
            >
              <span className="block h-8 w-px bg-current opacity-20" aria-hidden="true" />
              <span
                className={`block font-display font-extrabold tracking-tight text-[15px] leading-tight max-w-[220px] ${
                  scrolled ? "text-navy" : "text-white"
                }`}
              >
                Operations Transformation Program
              </span>
            </button>
          </div>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8" aria-label="Primary">
            {nav.map((item) => (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => go(item.id)}
                className={`link-underline text-sm font-medium transition-colors duration-300 ${
                  scrolled ? "text-navy hover:text-teal" : "text-white/90 hover:text-white"
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <a
              href={phoneHref}
              data-testid="header-phone"
              onClick={onPhone}
              className={`inline-flex items-center gap-2 whitespace-nowrap font-display font-semibold text-sm transition-colors duration-300 ${
                scrolled ? "text-navy hover:text-teal" : "text-white hover:text-teal"
              }`}
            >
              <Phone className="h-4 w-4" /> {brand.phone}
            </a>
            <CTAButton data-testid="header-prospectus-cta" onClick={requestProspectus}>
              {cta.prospectusShort}
            </CTAButton>
          </div>

          <div className="lg:hidden flex items-center gap-1">
            <a
              href={phoneHref}
              data-testid="header-phone-mobile"
              onClick={onPhone}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap font-display font-semibold text-xs px-2 py-2 rounded-sm focus-ring ${
                scrolled ? "text-navy" : "text-white"
              }`}
            >
              <Phone className="h-4 w-4" /> {brand.phone}
            </a>
            <button
              data-testid="mobile-menu-toggle"
              className={`p-2 focus-ring rounded-sm ${scrolled ? "text-navy" : "text-white"}`}
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-navy border-t border-hair-dark" data-testid="mobile-menu">
          <div className="container-page py-6 flex flex-col gap-1">
            {nav.map((item) => (
              <button
                key={item.id}
                data-testid={`mobile-nav-${item.id}`}
                onClick={() => go(item.id)}
                className="text-left text-white/90 py-3 border-b border-hair-dark text-base font-medium"
              >
                {item.label}
              </button>
            ))}
            <CTAButton className="mt-5 w-full" data-testid="mobile-prospectus-cta" onClick={requestProspectus}>
              {cta.prospectusShort}
            </CTAButton>
          </div>
        </div>
      )}
    </header>
  );
};
